import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { IconButton } from '@mui/material';
import React, {
  ChangeEvent,
  forwardRef,
  MouseEvent,
  Ref,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import imageIcon from '../../../assets/icons/icon_image.svg';
import { FileChunk, useDropFileChunk } from '../../../hooks/fileDndHook';
import { ColoredIcon } from '../Icon';

import { SlotWrapper, SlotWrapperProps } from './SlotWrapper';
import { useEnrollSlotRef } from './hooks';
import {
  FileItem,
  FileInputLabel,
  FileInput,
  FileItemNameContainer,
  FileItemName,
} from './styledComponents';
import { SlotRef } from './types';

const Placeholder = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #a3a6ab;
  word-break: keep-all;
`;

interface DicomUploadSlotProps extends SlotWrapperProps {
  padding?: string;
  textAlign?: string;
  maxLength?: number;
  placeholder?: string;
  fileChunks?: FileChunk[];
  updateFiles: (files: FileChunk[] | undefined) => void;
}
export const DicomUploadSlot = forwardRef(
  (
    {
      placeholder,
      updateFiles,
      fileChunks: controlledFileChunks,
      ...slotWrapperProps
    }: DicomUploadSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    useEnrollSlotRef({ ref, wrapperRef, inputs: [fileInputRef.current] });

    const { t } = useTranslation('translation', {
      keyPrefix: 'features.uploadImages',
    });

    const { fileChunks, addToFileChunks, deleteFromFileChunks, drop } =
      useDropFileChunk({
        controlledFileChunks,
        updateFiles,
      });
    useEffect(() => {
      updateFiles(fileChunks);
    }, [fileChunks, updateFiles]);

    const onUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.type !== 'drop') {
        const files = Array.from(e.target.files || []);
        if (files.length) {
          addToFileChunks(files);
        }
      }
    };

    const onClickDelete = (e: MouseEvent, id: string) => {
      e.preventDefault();
      deleteFromFileChunks(id);
    };

    const onClickUploader = (event: MouseEvent<HTMLInputElement>) => {
      event.currentTarget.value = '';
    };

    return (
      <SlotWrapper
        ref={wrapperRef}
        {...slotWrapperProps}
        icon={
          <ColoredIcon
            width="22px"
            height="18px"
            imageUrl={imageIcon}
            imageWidth="18px"
            color={'var(--HScan_Light_Blue, #2e5be0)'}
          />
        }>
        {drop(
          // only native element nodes can now be passed to React DnD connectors.
          // You can either wrap styled.div into a <div>, or turn it into a drag source or a drop target itself.
          <div style={{ width: '100%' }}>
            <FileInputLabel>
              <FileInput
                ref={fileInputRef}
                accept=".dcm"
                type="file"
                multiple
                onChange={onUploadFile}
                onClick={onClickUploader}
              />
              <Placeholder>{placeholder}</Placeholder>
              {fileChunks.map(
                fileChunk =>
                  fileChunk.files.length > 0 && (
                    <FileItem
                      key={fileChunk.id}
                      onClick={e => e.preventDefault()}>
                      <FileItemNameContainer>
                        <FileItemName>
                          {fileChunk.files[0].name}
                          {fileChunk.files.length > 2 &&
                            t('dicomCount', {
                              count: fileChunk.files.length - 1,
                            })}
                        </FileItemName>
                      </FileItemNameContainer>
                      <IconButton
                        size="small"
                        onClick={e => onClickDelete(e, fileChunk.id)}>
                        <DeleteForeverIcon />
                      </IconButton>
                    </FileItem>
                  ),
              )}
            </FileInputLabel>
          </div>,
        )}
      </SlotWrapper>
    );
  },
);
